// feature/carpetas-tracking — sprint79 + addendum #2. Working surface
// for the carpetas team. 4 tabs: pendientes / hechas / cobradas / todas.
// hecha_por is user-attributed per row — the dropdown in mark modals
// lets Natali (or anyone) attribute the work to its actual author,
// while the audit_log still preserves actor_id (who clicked the button).

import React, { useEffect, useMemo, useRef, useState } from "react";
import { carpetas as carpetasApi } from "../../api/endpoints";
import { apiBase } from "../../api/client";
import { useAuth } from "../../auth/AuthContext";
import { useToast } from "../../components/Toast";
import { rolesOf } from "../../utils/permissions";
import Spinner from "../../components/Spinner";
import Empty from "../../components/Empty";
import CenteredModal from "../../components/CenteredModal";
import { TipoPill } from "../../components/Pills";
import CaseDetail from "../case-detail";

const TIPO_LABEL = {
  tutela: "Tutela",
  fallo: "Fallo",
  requerimiento: "Requerimiento",
  desacato: "Desacato",
  sancion: "Sanción",
};

function fmtDate(d) {
  return d ? new Date(d).toLocaleDateString("es-CO") : "—";
}
function fmtDateTime(d) {
  return d ? new Date(d).toLocaleString("es-CO") : "—";
}

const TABS = [
  { key: "pendientes", label: "Pendientes" },
  { key: "hechas",     label: "Hechas" },
  { key: "cobradas",   label: "Ya cobradas" },
  { key: "todas",      label: "Todas" },
];

export default function CarpetasTracking() {
  const { user } = useAuth();
  const toast = useToast();
  const userRoles = rolesOf(user);
  const isAdmin = userRoles.includes("admin");
  const canWrite = isAdmin || userRoles.includes("carpetas");

  const [tab, setTab] = useState("pendientes");
  const [month, setMonth] = useState("");
  const [tipo, setTipo] = useState("");
  const [abogadoId, setAbogadoId] = useState("");
  const [juzgado, setJuzgado] = useState("");
  const [hechaPorId, setHechaPorId] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [expanded, setExpanded] = useState(null);

  // Mark modals — three variants depending on what's being done.
  const [markHechaCase, setMarkHechaCase] = useState(null);     // single mark-hecha from Pendientes
  const [markCobradaCase, setMarkCobradaCase] = useState(null); // single mark-ya-cobrada from Pendientes (creates carpeta with pagada=true)
  const [bulkHechaOpen, setBulkHechaOpen] = useState(false);
  const [bulkCobradaOpen, setBulkCobradaOpen] = useState(false);

  // Transition modals (hecha → cobrada) — no attribution dropdown.
  const [transitionCobradaCarpeta, setTransitionCobradaCarpeta] = useState(null);
  const [bulkTransitionCobradaOpen, setBulkTransitionCobradaOpen] = useState(false);

  // sprint79.1 — revert-cobrada modals (cobrada → hecha, carpeta stays).
  const [revertCobradaCarpeta, setRevertCobradaCarpeta] = useState(null);
  const [bulkRevertCobradaOpen, setBulkRevertCobradaOpen] = useState(false);

  const [revertCarpeta, setRevertCarpeta] = useState(null);
  const [openDetailId, setOpenDetailId] = useState(null);

  // Cached users list for the dropdown. Loaded once after canWrite is known.
  const [usersList, setUsersList] = useState(null);
  useEffect(() => {
    if (!canWrite) return;
    carpetasApi.usersList()
      .then(r => setUsersList(r.data || []))
      .catch(() => setUsersList([]));
  }, [canWrite]);

  // Undo toast — captures the most-recent bulk insert so the user can
  // unwind within 10 seconds.
  const [undo, setUndo] = useState(null);
  const undoTimerRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  function load() {
    setData(null);
    const params = { tab };
    if (month) params.month = month;
    if (tipo)  params.tipo = tipo;
    if (abogadoId) params.abogado_id = abogadoId;
    if (juzgado)   params.juzgado = juzgado;
    if (hechaPorId && tab !== "pendientes") params.hecha_por_id = hechaPorId;
    if (search) params.search = search;
    carpetasApi.tracking(params)
      .then(r => { setData(r.data); setSelected(new Set()); })
      .catch(() => { setData({ counts: { pendientes: 0, hechas: 0, cobradas: 0, todas: 0 }, filters_distinct: {}, rows: [] }); });
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [tab, month, tipo, abogadoId, juzgado, hechaPorId, search]);

  function resetFilters() {
    setMonth(""); setTipo(""); setAbogadoId("");
    setJuzgado(""); setHechaPorId(""); setSearchInput(""); setSearch("");
  }

  // Selection: valid on Pendientes (insert by case_id), Hechas (mark-
  // cobrada by carpeta_id), Ya cobradas (revert-cobrada by carpeta_id).
  // The semantics differ per tab so the bulk-bar buttons swap accordingly.
  const selectableRows = useMemo(() => {
    if (tab === "pendientes" || tab === "hechas" || tab === "cobradas") return data?.rows || [];
    return [];
  }, [data, tab]);

  function toggleSelect(id) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  function selectAll() {
    // Pendientes: by case_id (insert target). Hechas/Cobradas: by
    // carpeta_id (the row whose pagada we toggle).
    const idKey = (tab === "hechas" || tab === "cobradas") ? (r) => r.carpeta?.id : (r) => r.case_id;
    const ids = selectableRows.map(idKey).filter(Boolean);
    setSelected(new Set(ids));
  }
  function clearSelection() {
    setSelected(new Set());
  }

  function scheduleUndo(payload) {
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    setUndo(payload);
    undoTimerRef.current = setTimeout(() => setUndo(null), 10_000);
  }
  function dismissUndo() {
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    setUndo(null);
  }

  async function handleSingleMarkHecha(caseId, observaciones, hechaPorIdOverride) {
    try {
      const r = await carpetasApi.bulkMark([caseId], {
        observaciones: observaciones || null,
        mark_as: "hecha",
        hecha_por_id: hechaPorIdOverride || null,
      });
      const success = r.data.successes?.[0];
      if (success) {
        toast.push(`Carpeta ${success.numero_carpeta} marcada.`);
        scheduleUndo({ type: "single", revertable: "carpeta", carpeta_ids: [success.carpeta_id], case_number: markHechaCase?.case_number });
      } else {
        const fail = r.data.failures?.[0];
        toast.push(fail?.marked_by
          ? `Esta carpeta ya fue marcada por ${fail.marked_by}.`
          : "No se pudo marcar la carpeta.", "error");
      }
      setMarkHechaCase(null);
      load();
    } catch (e) {
      toast.push(e?.response?.data?.error || "Error al marcar", "error");
    }
  }

  async function handleSingleMarkCobrada(caseId, observaciones, hechaPorIdOverride) {
    try {
      const r = await carpetasApi.bulkMark([caseId], {
        observaciones: observaciones || null,
        mark_as: "cobrada",
        hecha_por_id: hechaPorIdOverride || null,
      });
      const success = r.data.successes?.[0];
      if (success) {
        toast.push(`Carpeta ${success.numero_carpeta} marcada como ya cobrada.`);
        scheduleUndo({ type: "single", revertable: "carpeta", carpeta_ids: [success.carpeta_id], case_number: markCobradaCase?.case_number });
      } else {
        const fail = r.data.failures?.[0];
        toast.push(fail?.marked_by
          ? `Esta carpeta ya fue marcada por ${fail.marked_by}.`
          : "No se pudo marcar la carpeta.", "error");
      }
      setMarkCobradaCase(null);
      load();
    } catch (e) {
      toast.push(e?.response?.data?.error || "Error al marcar", "error");
    }
  }

  async function handleBulkMark(markAs, observaciones, hechaPorIdOverride) {
    const caseIds = Array.from(selected);
    if (!caseIds.length) return;
    try {
      const r = await carpetasApi.bulkMark(caseIds, {
        observaciones: observaciones || null,
        mark_as: markAs,
        hecha_por_id: hechaPorIdOverride || null,
      });
      const ok = r.data.successes || [];
      const failed = r.data.failures || [];
      const label = markAs === "cobrada" ? "ya cobradas" : "hechas";
      let msg = `${ok.length} carpeta(s) marcadas como ${label}.`;
      if (failed.length) msg += ` ${failed.length} ya estaban marcadas.`;
      toast.push(msg);
      if (ok.length) {
        scheduleUndo({
          type: "bulk", revertable: "carpeta",
          carpeta_ids: ok.map(s => s.carpeta_id),
          count: ok.length, label,
        });
      }
      setBulkHechaOpen(false);
      setBulkCobradaOpen(false);
      load();
    } catch (e) {
      toast.push(e?.response?.data?.error || "Error al marcar", "error");
    }
  }

  async function handleTransitionToCobrada(carpetaId, caseNumber, observaciones) {
    try {
      await carpetasApi.markCobrada(carpetaId, observaciones || null);
      toast.push(`Pago confirmado para ${caseNumber}.`);
      // sprint79.1 — undo flips pagada back via revert-cobrada (not the
      // destructive revert that deletes the carpeta).
      scheduleUndo({ type: "single", revertable: "cobrada", carpeta_ids: [carpetaId], case_number: caseNumber });
      setTransitionCobradaCarpeta(null);
      load();
    } catch (e) {
      toast.push(e?.response?.data?.error || "Error al marcar como cobrada", "error");
    }
  }

  async function handleBulkTransitionToCobrada() {
    const carpetaIds = Array.from(selected);
    if (!carpetaIds.length) return;
    try {
      const r = await carpetasApi.bulkMarkCobrada(carpetaIds);
      const ok = r.data.successes || [];
      const failed = r.data.failures || [];
      let msg = `${ok.length} carpeta(s) marcadas como cobradas.`;
      if (failed.length) msg += ` ${failed.length} no se pudieron actualizar.`;
      toast.push(msg);
      if (ok.length) {
        scheduleUndo({
          type: "bulk", revertable: "cobrada",
          carpeta_ids: ok.map(s => s.carpeta_id),
          count: ok.length, label: "cobradas",
        });
      }
      setBulkTransitionCobradaOpen(false);
      load();
    } catch (e) {
      toast.push(e?.response?.data?.error || "Error al actualizar", "error");
    }
  }

  // sprint79.1 — revert-cobrada flips pagada back to false; carpeta row stays.
  async function handleRevertCobrada(carpetaId, caseNumber) {
    try {
      await carpetasApi.revertCobrada(carpetaId);
      toast.push("Cobro revertido.");
      scheduleUndo({ type: "single", revertable: "cobrada-revert", carpeta_ids: [carpetaId], case_number: caseNumber });
      setRevertCobradaCarpeta(null);
      load();
    } catch (e) {
      toast.push(e?.response?.data?.error || "Error al revertir cobro", "error");
    }
  }

  async function handleBulkRevertCobrada() {
    const carpetaIds = Array.from(selected);
    if (!carpetaIds.length) return;
    try {
      const r = await carpetasApi.bulkRevertCobrada(carpetaIds);
      const ok = r.data.successes || [];
      const failed = r.data.failures || [];
      let msg = `${ok.length} cobro(s) revertido(s).`;
      if (failed.length) msg += ` ${failed.length} no se pudieron actualizar.`;
      toast.push(msg);
      if (ok.length) {
        scheduleUndo({
          type: "bulk", revertable: "cobrada-revert",
          carpeta_ids: ok.map(s => s.carpeta_id),
          count: ok.length, label: "cobros revertidos",
        });
      }
      setBulkRevertCobradaOpen(false);
      load();
    } catch (e) {
      toast.push(e?.response?.data?.error || "Error al revertir cobros", "error");
    }
  }

  async function handleUndo() {
    if (!undo) return;
    try {
      let undone = 0;
      for (const cid of undo.carpeta_ids) {
        try {
          if (undo.revertable === "cobrada") {
            // sprint79.1 — undo cobrada transition flips pagada back.
            await carpetasApi.revertCobrada(cid); undone++;
          } else if (undo.revertable === "cobrada-revert") {
            // sprint79.1 — undo revert-cobrada re-applies cobrada.
            await carpetasApi.markCobrada(cid, null); undone++;
          } else {
            // Default: full revert (removes the carpeta row).
            await carpetasApi.revert(cid); undone++;
          }
        } catch (e) { /* may have been reverted via UI; keep going */ }
      }
      const verb = undo.revertable === "cobrada"
        ? (undone === 1 ? "Cobro revertido." : `${undone} cobros revertidos.`)
        : undo.revertable === "cobrada-revert"
          ? (undone === 1 ? "Cobro restaurado." : `${undone} cobros restaurados.`)
          : (undone === 1 ? "Carpeta revertida." : `${undone} carpetas revertidas.`);
      toast.push(verb);
      dismissUndo();
      load();
    } catch (e) {
      toast.push("No se pudo deshacer", "error");
    }
  }

  async function handleRevert() {
    if (!revertCarpeta) return;
    try {
      await carpetasApi.revert(revertCarpeta.carpeta_id);
      toast.push("Carpeta revertida.");
      setRevertCarpeta(null);
      load();
    } catch (e) {
      const msg = e?.response?.status === 403
        ? "Solo puede revertir carpetas que usted marcó."
        : (e?.response?.data?.error || "Error al revertir");
      toast.push(msg, "error");
    }
  }

  const fd = data?.filters_distinct || {};
  const rows = data?.rows || [];
  const counts = data?.counts || { pendientes: 0, hechas: 0, cobradas: 0, todas: 0 };
  const hasAnyFilter = !!(month || tipo || abogadoId || juzgado || hechaPorId || search);
  const csvUrl = apiBase + carpetasApi.trackingCsvUrl({
    tab, month, tipo, abogado_id: abogadoId, juzgado,
    hecha_por_id: (tab !== "pendientes") ? hechaPorId : "",
    search,
  });

  return (
    <div className="col" style={{ gap: 16 }}>
      <div>
        <h1 style={{ margin: 0 }}>Carpetas — Trabajo del equipo</h1>
        <div className="muted" style={{ marginTop: 4, fontSize: 13 }}>
          Casos contestados que necesitan carpeta. Marque cada uno como hecho cuando termine el armado en Drive.
        </div>
      </div>

      <div className="row" style={{ gap: 4, borderBottom: "1px solid var(--border-hairline)" }}>
        {TABS.map(t => (
          <button key={t.key}
                  type="button"
                  role="tab"
                  aria-selected={tab === t.key}
                  onClick={() => { setTab(t.key); setSelected(new Set()); }}
                  style={{
                    padding: "10px 16px",
                    background: "transparent",
                    border: "none",
                    borderBottom: `2px solid ${tab === t.key ? "var(--accent-mint)" : "transparent"}`,
                    fontSize: 13,
                    fontWeight: tab === t.key ? 600 : 500,
                    color: tab === t.key ? "var(--accent-mint)" : "var(--text-muted)",
                    cursor: "pointer",
                    marginBottom: -1,
                  }}>
            {t.label} <span className="muted" style={{ fontWeight: 400, marginLeft: 4 }}>({counts[t.key] ?? 0})</span>
          </button>
        ))}
      </div>

      <div className="card tight" style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
        <div style={{ flex: "1 1 240px" }}>
          <label>Buscar (radicado o accionante, exacto)</label>
          <input type="search"
                 value={searchInput}
                 onChange={(e) => setSearchInput(e.target.value)}
                 placeholder="13001-31-... o Juan Pérez" />
        </div>
        <div>
          <label>Mes</label>
          <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
        </div>
        <div>
          <label>Tipo</label>
          <select value={tipo} onChange={(e) => setTipo(e.target.value)}>
            <option value="">Todos los tipos</option>
            {(fd.tipos || []).map(t => <option key={t} value={t}>{TIPO_LABEL[t] || t}</option>)}
          </select>
        </div>
        <div>
          <label>Abogado</label>
          <select value={abogadoId} onChange={(e) => setAbogadoId(e.target.value)}>
            <option value="">Todos los abogados</option>
            {(fd.abogados || []).map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </div>
        <div>
          <label>Juzgado</label>
          <select value={juzgado} onChange={(e) => setJuzgado(e.target.value)}>
            <option value="">Todos los juzgados</option>
            {(fd.juzgados || []).map(j => <option key={j} value={j}>{j}</option>)}
          </select>
        </div>
        {tab !== "pendientes" && (
          <div>
            <label>Hecha por</label>
            <select value={hechaPorId} onChange={(e) => setHechaPorId(e.target.value)}>
              <option value="">Todos</option>
              {(fd.carpetas_team || []).map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </div>
        )}
        {hasAnyFilter && (
          <button className="ghost sm" onClick={resetFilters} style={{ alignSelf: "flex-end" }}>
            Limpiar filtros
          </button>
        )}
        <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "flex-end" }}>
          <a href={csvUrl} className="ghost sm" download
             style={{ padding: "6px 12px", border: "1px solid var(--border-hairline)", borderRadius: 4, textDecoration: "none" }}>
            Descargar CSV
          </a>
        </div>
      </div>

      <div className="card" style={{ padding: 0 }}>
        {data == null ? (
          <div style={{ padding: 32, textAlign: "center" }}><Spinner size={20} /></div>
        ) : rows.length === 0 ? (
          <EmptyState tab={tab} hasAnyFilter={hasAnyFilter} onResetFilters={resetFilters} />
        ) : (
          <ResultsTable
            tab={tab}
            rows={rows}
            selected={selected}
            selectableRows={selectableRows}
            canWrite={canWrite}
            isAdmin={isAdmin}
            currentUserId={user?.id}
            expanded={expanded}
            onToggleExpand={(id) => setExpanded(prev => prev === id ? null : id)}
            onToggleSelect={toggleSelect}
            onSelectAll={selectAll}
            onClearSelection={clearSelection}
            onMarkHechaOne={(row) => setMarkHechaCase(row)}
            onMarkCobradaOne={(row) => setMarkCobradaCase(row)}
            onTransitionCobradaOne={(row) => setTransitionCobradaCarpeta({
              carpeta_id: row.carpeta.id,
              case_id: row.case_id,
              case_number: row.case_number,
              hecha_at: row.carpeta.hecha_at,
              hecha_por_name: row.carpeta.hecha_por?.nombre || null,
            })}
            onRevertCobradaOne={(row) => setRevertCobradaCarpeta({
              carpeta_id: row.carpeta.id,
              case_id: row.case_id,
              case_number: row.case_number,
              pagada_at: row.carpeta.pagada_at,
            })}
            onRevert={(row) => setRevertCarpeta({
              carpeta_id: row.carpeta.id,
              case_id: row.case_id,
              case_number: row.case_number,
              hecha_at: row.carpeta.hecha_at,
            })}
            onOpenDetail={(id) => setOpenDetailId(id)}
          />
        )}
      </div>

      {/* Bulk action bar — semantics depend on tab */}
      {selected.size > 0 && canWrite && tab === "pendientes" && (
        <div style={bulkBarStyle}>
          <strong>{selected.size}</strong> seleccionada(s)
          <button onClick={() => setBulkHechaOpen(true)}>Marcar como hechas</button>
          <button className="secondary" onClick={() => setBulkCobradaOpen(true)}>Marcar como ya cobradas</button>
          <button className="ghost sm" onClick={clearSelection}>Limpiar</button>
        </div>
      )}
      {selected.size > 0 && canWrite && tab === "hechas" && (
        <div style={bulkBarStyle}>
          <strong>{selected.size}</strong> seleccionada(s)
          <button onClick={() => setBulkTransitionCobradaOpen(true)}>Marcar como cobradas</button>
          <button className="ghost sm" onClick={clearSelection}>Limpiar</button>
        </div>
      )}
      {selected.size > 0 && canWrite && tab === "cobradas" && (
        <div style={bulkBarStyle}>
          <strong>{selected.size}</strong> seleccionada(s)
          <button onClick={() => setBulkRevertCobradaOpen(true)}>
            Revertir cobro de {selected.size}
          </button>
          <button className="ghost sm" onClick={clearSelection}>Limpiar</button>
        </div>
      )}

      {/* Single mark-hecha (from Pendientes) */}
      {markHechaCase && (
        <MarkHechaModal
          row={markHechaCase}
          usersList={usersList}
          currentUserId={user?.id}
          onCancel={() => setMarkHechaCase(null)}
          onConfirm={(obs, attrId) => handleSingleMarkHecha(markHechaCase.case_id, obs, attrId)}
        />
      )}

      {/* Single mark-ya-cobrada (from Pendientes, backfill flow) */}
      {markCobradaCase && (
        <MarkYaCobradaModal
          row={markCobradaCase}
          usersList={usersList}
          currentUserId={user?.id}
          onCancel={() => setMarkCobradaCase(null)}
          onConfirm={(obs, attrId) => handleSingleMarkCobrada(markCobradaCase.case_id, obs, attrId)}
        />
      )}

      {/* Bulk mark-hecha (from Pendientes) */}
      {bulkHechaOpen && (
        <BulkMarkModal
          mode="hecha"
          rows={(data?.rows || []).filter(r => selected.has(r.case_id))}
          usersList={usersList}
          currentUserId={user?.id}
          onCancel={() => setBulkHechaOpen(false)}
          onConfirm={(obs, attrId) => handleBulkMark("hecha", obs, attrId)}
        />
      )}

      {/* Bulk mark-ya-cobrada (from Pendientes, backfill flow) */}
      {bulkCobradaOpen && (
        <BulkMarkModal
          mode="cobrada"
          rows={(data?.rows || []).filter(r => selected.has(r.case_id))}
          usersList={usersList}
          currentUserId={user?.id}
          onCancel={() => setBulkCobradaOpen(false)}
          onConfirm={(obs, attrId) => handleBulkMark("cobrada", obs, attrId)}
        />
      )}

      {/* Single transition hecha → cobrada (from Hechas tab) */}
      {transitionCobradaCarpeta && (
        <TransitionCobradaModal
          payload={transitionCobradaCarpeta}
          onCancel={() => setTransitionCobradaCarpeta(null)}
          onConfirm={(obs) => handleTransitionToCobrada(
            transitionCobradaCarpeta.carpeta_id,
            transitionCobradaCarpeta.case_number,
            obs
          )}
        />
      )}

      {/* Bulk transition hecha → cobrada (from Hechas tab) */}
      {bulkTransitionCobradaOpen && (
        <BulkTransitionCobradaModal
          count={selected.size}
          onCancel={() => setBulkTransitionCobradaOpen(false)}
          onConfirm={handleBulkTransitionToCobrada}
        />
      )}

      {/* sprint79.1 — single revert-cobrada (from Ya cobradas tab) */}
      {revertCobradaCarpeta && (
        <RevertCobradaModal
          payload={revertCobradaCarpeta}
          onCancel={() => setRevertCobradaCarpeta(null)}
          onConfirm={() => handleRevertCobrada(
            revertCobradaCarpeta.carpeta_id,
            revertCobradaCarpeta.case_number
          )}
        />
      )}

      {/* sprint79.1 — bulk revert-cobrada (from Ya cobradas tab) */}
      {bulkRevertCobradaOpen && (
        <BulkRevertCobradaModal
          count={selected.size}
          onCancel={() => setBulkRevertCobradaOpen(false)}
          onConfirm={handleBulkRevertCobrada}
        />
      )}

      {revertCarpeta && (
        <RevertModal
          payload={revertCarpeta}
          onCancel={() => setRevertCarpeta(null)}
          onConfirm={handleRevert}
        />
      )}

      {undo && (
        <div style={undoToastStyle}>
          <span>
            {undo.type === "single"
              ? `Carpeta marcada${undo.case_number ? ` para ${undo.case_number}` : ""}.`
              : `${undo.count} carpetas marcadas${undo.label ? ` como ${undo.label}` : ""}.`}
          </span>
          <button type="button" onClick={handleUndo}
                  style={{ background: "transparent", color: "var(--accent-mint)",
                           border: "none", cursor: "pointer", fontWeight: 600 }}>
            {undo.type === "single" ? "Deshacer" : "Deshacer todas"}
          </button>
          <button type="button" onClick={dismissUndo}
                  aria-label="Cerrar"
                  style={{ background: "transparent", color: "white", opacity: 0.6,
                           border: "none", cursor: "pointer", fontSize: 18 }}>×</button>
        </div>
      )}

      <CaseDetail
        caseId={openDetailId}
        open={!!openDetailId}
        onClose={() => setOpenDetailId(null)}
        onChanged={load}
      />
    </div>
  );
}

const bulkBarStyle = {
  position: "sticky", bottom: 16, alignSelf: "center",
  background: "var(--bg-elevated)",
  border: "1px solid var(--accent-mint)",
  borderRadius: 8, padding: "10px 16px",
  display: "flex", gap: 12, alignItems: "center",
  boxShadow: "0 6px 24px rgba(0,0,0,0.12)",
};
const undoToastStyle = {
  position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)",
  background: "var(--text-primary)", color: "white",
  padding: "10px 16px", borderRadius: 8,
  display: "flex", gap: 16, alignItems: "center",
  boxShadow: "0 4px 12px rgba(0,0,0,0.25)", zIndex: 50,
};

// ---------------------------------------------------------------------------
// EmptyState
function EmptyState({ tab, hasAnyFilter, onResetFilters }) {
  if (hasAnyFilter) {
    return (
      <div style={{ padding: 32, textAlign: "center" }}>
        <Empty title="Ningún caso coincide con los filtros aplicados." />
        <button className="ghost sm" onClick={onResetFilters} style={{ marginTop: 12 }}>
          Limpiar filtros
        </button>
      </div>
    );
  }
  if (tab === "pendientes") {
    return (
      <div style={{ padding: 32, textAlign: "center" }}>
        <div style={{ fontSize: 32, marginBottom: 8 }}>✨</div>
        <div style={{ fontSize: 14 }}>No hay casos pendientes de carpeta. ¡Buen trabajo!</div>
      </div>
    );
  }
  if (tab === "hechas") {
    return (
      <Empty title="Aún no hay carpetas hechas pendientes de cobro."
             message="Cuando confirme la primera, aparecerá aquí." />
    );
  }
  if (tab === "cobradas") {
    return (
      <Empty title="Aún no hay carpetas marcadas como cobradas."
             message="Cuando cobre la primera, aparecerá aquí." />
    );
  }
  return <Empty title="Sin resultados." />;
}

// ---------------------------------------------------------------------------
// UserDropdown — searchable. Used by the mark-hecha + mark-cobrada
// modals for attribution override. Carpetas-role users come first.
function UserDropdown({ value, onChange, usersList, currentUserId, disabled }) {
  const [search, setSearch] = useState("");
  const filtered = (usersList || []).filter(u =>
    !search.trim() || u.name.toLowerCase().includes(search.toLowerCase().trim()));
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <select value={value || ""}
              onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
              disabled={disabled || !usersList}
              style={{ width: "100%" }}>
        {!usersList && <option>Cargando usuarios…</option>}
        {usersList && filtered.length === 0 && <option value="">— Sin resultados —</option>}
        {usersList && filtered.map(u => (
          <option key={u.id} value={u.id}>
            {u.name}{u.has_carpetas_role ? " · carpetas" : ""}
            {u.id === currentUserId ? " (usted)" : ""}
          </option>
        ))}
      </select>
      {usersList && usersList.length > 8 && (
        <input type="search" placeholder="Filtrar usuarios…"
               value={search} onChange={(e) => setSearch(e.target.value)}
               disabled={disabled}
               style={{ fontSize: 12 }} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ResultsTable
function ResultsTable({
  tab, rows, selected, selectableRows, canWrite, isAdmin, currentUserId,
  expanded, onToggleExpand, onToggleSelect, onSelectAll, onClearSelection,
  onMarkHechaOne, onMarkCobradaOne, onTransitionCobradaOne, onRevertCobradaOne, onRevert, onOpenDetail,
}) {
  // Selection-id strategy varies by tab; mirror what selectAll uses.
  const idOfRow = (tab === "hechas" || tab === "cobradas") ? (r) => r.carpeta?.id : (r) => r.case_id;
  const selectableSelected = selectableRows.length > 0
    && selectableRows.every(r => selected.has(idOfRow(r)));
  const showHechaPorColumn = tab !== "pendientes";

  return (
    <table>
      <thead>
        <tr>
          {(tab === "pendientes" || tab === "hechas" || tab === "cobradas") && canWrite && (
            <th style={{ width: 32 }}>
              <input type="checkbox"
                     checked={selectableSelected}
                     onChange={() => selectableSelected ? onClearSelection() : onSelectAll()}
                     aria-label="Seleccionar todas" />
            </th>
          )}
          <th>Caso</th>
          <th>Tipo</th>
          <th>Accionante</th>
          <th>Radicado</th>
          <th>Juzgado</th>
          <th>Abogado</th>
          <th>Contestada</th>
          {showHechaPorColumn && <th>Hecha por</th>}
          {tab === "todas" && <th>Estado carpeta</th>}
          <th></th>
        </tr>
      </thead>
      <tbody>
        {rows.map(row => {
          const isOpen = expanded === row.case_id;
          const isHecha = !!row.carpeta;
          const isCobrada = isHecha && row.carpeta.pagada;
          const canRevertThis = canWrite && isHecha
            && (isAdmin || row.carpeta.hecha_por?.id === currentUserId);
          const selectionId = idOfRow(row);
          return (
            <React.Fragment key={row.case_id}>
              <tr>
                {(tab === "pendientes" || tab === "hechas" || tab === "cobradas") && canWrite && (
                  <td onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox"
                           checked={!!selectionId && selected.has(selectionId)}
                           onChange={() => selectionId && onToggleSelect(selectionId)}
                           disabled={!selectionId}
                           aria-label={`Seleccionar ${row.case_number}`} />
                  </td>
                )}
                <td onClick={() => onToggleExpand(row.case_id)} style={{ cursor: "pointer" }}>
                  <strong>{row.case_number}</strong>
                  <span className="muted" style={{ marginLeft: 6, fontSize: 11 }}>
                    {isOpen ? "▾" : "▸"}
                  </span>
                </td>
                <td onClick={() => onToggleExpand(row.case_id)} style={{ cursor: "pointer" }}>
                  <TipoPill tipo={row.tipo} />
                </td>
                <td onClick={() => onToggleExpand(row.case_id)} style={{ cursor: "pointer" }}>
                  {row.accionante?.nombre || "—"}
                </td>
                <td onClick={() => onToggleExpand(row.case_id)} style={{ cursor: "pointer" }}
                    className="mono" title={row.radicado || ""}>
                  <span style={{ fontSize: 12 }}>{row.radicado || "—"}</span>
                </td>
                <td onClick={() => onToggleExpand(row.case_id)} style={{ cursor: "pointer", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                    title={row.juzgado || ""}>
                  {row.juzgado || "—"}
                </td>
                <td onClick={() => onToggleExpand(row.case_id)} style={{ cursor: "pointer" }}>
                  {row.abogado?.nombre || "—"}
                </td>
                <td onClick={() => onToggleExpand(row.case_id)} style={{ cursor: "pointer" }}>
                  {fmtDate(row.contestada_at)}
                </td>
                {showHechaPorColumn && (
                  <td onClick={() => onToggleExpand(row.case_id)} style={{ cursor: "pointer" }}>
                    {row.carpeta?.hecha_por?.nombre || "—"}
                  </td>
                )}
                {tab === "todas" && (
                  <td onClick={() => onToggleExpand(row.case_id)} style={{ cursor: "pointer" }}>
                    {isCobrada ? (
                      <span className="badge mint" style={{ fontSize: 11 }}>
                        Cobrada · {row.carpeta.numero_carpeta}
                      </span>
                    ) : isHecha ? (
                      <span className="badge info" style={{ fontSize: 11 }}>
                        Hecha · {row.carpeta.numero_carpeta}
                      </span>
                    ) : (
                      <span className="badge muted" style={{ fontSize: 11 }}>Pendiente</span>
                    )}
                  </td>
                )}
                <td onClick={(e) => e.stopPropagation()} style={{ whiteSpace: "nowrap" }}>
                  {canWrite && tab === "pendientes" && !isHecha && (
                    <>
                      <button className="sm" onClick={() => onMarkHechaOne(row)}>Marcar hecha</button>
                      {" "}
                      <button className="ghost sm" onClick={() => onMarkCobradaOne(row)}>
                        Marcar ya cobrada
                      </button>
                    </>
                  )}
                  {canWrite && tab === "hechas" && isHecha && !isCobrada && (
                    <>
                      <button className="sm" onClick={() => onTransitionCobradaOne(row)}>Marcar cobrada</button>
                      {" "}
                      <button className="ghost sm"
                              disabled={!canRevertThis}
                              title={canRevertThis ? "" : "Solo puede revertir sus propias carpetas."}
                              onClick={() => onRevert(row)}>
                        Revertir
                      </button>
                    </>
                  )}
                  {canWrite && tab === "cobradas" && isHecha && (
                    <>
                      <button className="sm" onClick={() => onRevertCobradaOne(row)}>
                        Revertir cobro
                      </button>
                      {" "}
                      <button className="ghost sm"
                              disabled={!canRevertThis}
                              title={canRevertThis ? "" : "Solo puede revertir sus propias carpetas."}
                              onClick={() => onRevert(row)}>
                        Revertir todo
                      </button>
                    </>
                  )}
                  {canWrite && tab === "todas" && isHecha && (
                    <button className="ghost sm"
                            disabled={!canRevertThis}
                            title={canRevertThis ? "" : "Solo puede revertir sus propias carpetas."}
                            onClick={() => onRevert(row)}>
                      Revertir
                    </button>
                  )}
                </td>
              </tr>
              {isOpen && (
                <tr>
                  <td colSpan={(tab === "pendientes" || tab === "hechas" || tab === "cobradas") ? 10 : 11} style={{ padding: 0 }}>
                    <ExpandedDetail row={row} onOpenDetail={onOpenDetail} />
                  </td>
                </tr>
              )}
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  );
}

// ---------------------------------------------------------------------------
// ExpandedDetail
function ExpandedDetail({ row, onOpenDetail }) {
  const a = row.accionante || {};
  const pf = row.previsora_fields || {};
  const k = row.carpeta;
  return (
    <div style={{
      padding: 16, background: "var(--bg-subtle)",
      borderTop: "1px solid var(--border-hairline)",
      borderBottom: "1px solid var(--border-hairline)",
    }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
        <section>
          <h4 style={dlH4}>Accionante</h4>
          <dl className="kvs" style={{ fontSize: 13, gridTemplateColumns: "120px 1fr" }}>
            <dt>Nombre</dt>   <dd>{a.nombre || "—"}</dd>
            <dt>Cédula</dt>   <dd>{a.cedula || "—"}</dd>
            <dt>Correo</dt>   <dd>{a.email || "—"}</dd>
            <dt>Teléfono</dt> <dd>{a.telefono || "—"}</dd>
          </dl>
        </section>
        <section>
          <h4 style={dlH4}>Datos del proceso</h4>
          <dl className="kvs" style={{ fontSize: 13, gridTemplateColumns: "150px 1fr" }}>
            <dt>Departamento</dt>          <dd>{pf.departamento || "—"}</dd>
            <dt>Municipio</dt>             <dd>{pf.municipio || "—"}</dd>
            <dt>Accionado</dt>             <dd>{pf.accionado_previsora || "—"}</dd>
            <dt>Vinculado principal</dt>   <dd>{pf.vinculado_principal || "—"}</dd>
            <dt>Vinculados</dt>            <dd>{pf.vinculados || "—"}</dd>
            <dt>Póliza SOAT</dt>           <dd>{pf.poliza_soat || "—"}</dd>
            <dt>Placa</dt>                 <dd>{pf.placa || "—"}</dd>
            <dt>N° siniestro</dt>          <dd>{pf.numero_siniestro || "—"}</dd>
            <dt>Fecha siniestro</dt>       <dd>{fmtDate(pf.fecha_siniestro)}</dd>
            <dt>Temática</dt>              <dd>{pf.tematica || "—"}</dd>
          </dl>
        </section>
      </div>
      {k && (
        <section style={{ marginTop: 16, paddingTop: 12, borderTop: "1px dashed var(--border-hairline)" }}>
          <h4 style={dlH4}>Carpeta</h4>
          <dl className="kvs" style={{ fontSize: 13, gridTemplateColumns: "150px 1fr" }}>
            <dt>N° carpeta</dt>      <dd>{k.numero_carpeta}</dd>
            <dt>Estado</dt>          <dd>{k.pagada
              ? <span className="badge mint" style={{ fontSize: 11 }}>Cobrada</span>
              : <span className="badge info" style={{ fontSize: 11 }}>Hecha</span>}
            </dd>
            <dt>Hecha por</dt>       <dd>{k.hecha_por?.nombre || "—"}</dd>
            <dt>Hecha el</dt>        <dd>{fmtDateTime(k.hecha_at)}</dd>
            {k.pagada && (<><dt>Cobrada el</dt><dd>{fmtDateTime(k.pagada_at)}</dd></>)}
            {k.observaciones && (<><dt>Observaciones</dt><dd style={{ whiteSpace: "pre-wrap" }}>{k.observaciones}</dd></>)}
          </dl>
        </section>
      )}
      <div style={{ marginTop: 12, textAlign: "right" }}>
        <button className="ghost sm" onClick={() => onOpenDetail(row.case_id)}>
          Abrir caso completo →
        </button>
      </div>
    </div>
  );
}
const dlH4 = {
  margin: "0 0 8px", fontSize: 12, fontWeight: 600,
  textTransform: "uppercase", letterSpacing: "0.4px",
  color: "var(--text-muted)",
};

// ---------------------------------------------------------------------------
// MarkHechaModal — single, with attribution dropdown
function MarkHechaModal({ row, usersList, currentUserId, onCancel, onConfirm }) {
  const [obs, setObs] = useState("");
  const [attrId, setAttrId] = useState(currentUserId || null);
  const [busy, setBusy] = useState(false);
  return (
    <CenteredModal
      open
      onClose={busy ? undefined : onCancel}
      title={`¿Confirmar carpeta hecha para ${row.case_number}?`}
      size="md"
      footer={<>
        <button className="secondary" disabled={busy} onClick={onCancel}>Cancelar</button>
        <button disabled={busy}
                onClick={async () => { setBusy(true); await onConfirm(obs, attrId); setBusy(false); }}>
          {busy && <Spinner color="currentColor" />} Confirmar
        </button>
      </>}
    >
      <dl className="kvs" style={{ fontSize: 13, gridTemplateColumns: "120px 1fr" }}>
        <dt>Accionante</dt> <dd>{row.accionante?.nombre || "—"}</dd>
        <dt>Radicado</dt>   <dd className="mono" style={{ fontSize: 12 }}>{row.radicado || "—"}</dd>
      </dl>
      <label style={{ display: "block", marginTop: 16, fontSize: 13 }}>
        Hecha por
        <UserDropdown value={attrId} onChange={setAttrId}
                      usersList={usersList} currentUserId={currentUserId} disabled={busy} />
      </label>
      <label style={{ display: "block", marginTop: 12, fontSize: 13 }}>
        Observaciones (opcional)
        <textarea value={obs} onChange={(e) => setObs(e.target.value)}
                  rows={3} maxLength={500} disabled={busy}
                  style={{ width: "100%", marginTop: 4 }} />
      </label>
    </CenteredModal>
  );
}

// ---------------------------------------------------------------------------
// MarkYaCobradaModal — single, backfill path (from Pendientes, marks as
// hecha AND pagada=true in one shot)
function MarkYaCobradaModal({ row, usersList, currentUserId, onCancel, onConfirm }) {
  const [obs, setObs] = useState("");
  const [attrId, setAttrId] = useState(currentUserId || null);
  const [busy, setBusy] = useState(false);
  return (
    <CenteredModal
      open
      onClose={busy ? undefined : onCancel}
      title={`¿Marcar ${row.accionante?.nombre || row.case_number} como ya cobrada?`}
      size="md"
      footer={<>
        <button className="secondary" disabled={busy} onClick={onCancel}>Cancelar</button>
        <button disabled={busy}
                onClick={async () => { setBusy(true); await onConfirm(obs, attrId); setBusy(false); }}>
          {busy && <Spinner color="currentColor" />} Confirmar
        </button>
      </>}
    >
      <p className="muted" style={{ fontSize: 12, margin: "0 0 12px" }}>
        Esto registra que la carpeta ya fue hecha y cobrada anteriormente.
      </p>
      <dl className="kvs" style={{ fontSize: 13, gridTemplateColumns: "120px 1fr" }}>
        <dt>Caso</dt>       <dd>{row.case_number}</dd>
        <dt>Accionante</dt> <dd>{row.accionante?.nombre || "—"}</dd>
        <dt>Radicado</dt>   <dd className="mono" style={{ fontSize: 12 }}>{row.radicado || "—"}</dd>
      </dl>
      <label style={{ display: "block", marginTop: 16, fontSize: 13 }}>
        Hecha por <span className="muted" style={{ fontWeight: 400 }}>(quién hizo la carpeta históricamente)</span>
        <UserDropdown value={attrId} onChange={setAttrId}
                      usersList={usersList} currentUserId={currentUserId} disabled={busy} />
      </label>
      <label style={{ display: "block", marginTop: 12, fontSize: 13 }}>
        Observaciones (opcional)
        <textarea value={obs} onChange={(e) => setObs(e.target.value)}
                  rows={3} maxLength={500} disabled={busy}
                  placeholder='Ej. "Cobrada en mayo 2025 antes de Athena"'
                  style={{ width: "100%", marginTop: 4 }} />
      </label>
    </CenteredModal>
  );
}

// ---------------------------------------------------------------------------
// BulkMarkModal — same dropdown applies to all selected rows.
// mode: "hecha" | "cobrada"
function BulkMarkModal({ mode, rows, usersList, currentUserId, onCancel, onConfirm }) {
  const [obs, setObs] = useState("");
  const [attrId, setAttrId] = useState(currentUserId || null);
  const [busy, setBusy] = useState(false);
  const preview = rows.slice(0, 5);
  const titleVerb = mode === "cobrada" ? "como ya cobradas" : "como hechas";
  const ctaLabel = `Confirmar las ${rows.length}`;
  return (
    <CenteredModal
      open
      onClose={busy ? undefined : onCancel}
      title={`¿Marcar ${rows.length} carpetas ${titleVerb}?`}
      size="md"
      footer={<>
        <button className="secondary" disabled={busy} onClick={onCancel}>Cancelar</button>
        <button disabled={busy}
                onClick={async () => { setBusy(true); await onConfirm(obs, attrId); setBusy(false); }}>
          {busy && <Spinner color="currentColor" />} {ctaLabel}
        </button>
      </>}
    >
      {mode === "cobrada" && (
        <p className="muted" style={{ fontSize: 12, margin: "0 0 12px" }}>
          Esto registra que cada carpeta ya fue hecha y cobrada anteriormente.
        </p>
      )}
      <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
        Casos seleccionados:
      </div>
      <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13 }}>
        {preview.map(r => (
          <li key={r.case_id}>
            <strong>{r.case_number}</strong>
            <span className="muted"> · {r.accionante?.nombre || "—"}</span>
          </li>
        ))}
        {rows.length > preview.length && (
          <li className="muted">y {rows.length - preview.length} más</li>
        )}
      </ul>
      <label style={{ display: "block", marginTop: 16, fontSize: 13 }}>
        Hecha por <span className="muted" style={{ fontWeight: 400 }}>(aplica a todas)</span>
        <UserDropdown value={attrId} onChange={setAttrId}
                      usersList={usersList} currentUserId={currentUserId} disabled={busy} />
      </label>
      <label style={{ display: "block", marginTop: 12, fontSize: 13 }}>
        Observaciones (opcional, aplica a todas)
        <textarea value={obs} onChange={(e) => setObs(e.target.value)}
                  rows={3} maxLength={500} disabled={busy}
                  style={{ width: "100%", marginTop: 4 }} />
      </label>
    </CenteredModal>
  );
}

// ---------------------------------------------------------------------------
// TransitionCobradaModal — hecha → cobrada. No attribution dropdown
// (the carpeta already carries hecha_por from when it was first marked
// hecha; this just flips pagada). sprint79.1 — optional observaciones
// appended with a [Cobrada YYYY-MM-DD] marker on the backend.
function TransitionCobradaModal({ payload, onCancel, onConfirm }) {
  const [obs, setObs] = useState("");
  const [busy, setBusy] = useState(false);
  return (
    <CenteredModal
      open
      onClose={busy ? undefined : onCancel}
      title={`¿Confirmar pago recibido para ${payload.case_number}?`}
      size="md"
      footer={<>
        <button className="secondary" disabled={busy} onClick={onCancel}>Cancelar</button>
        <button disabled={busy}
                onClick={async () => { setBusy(true); await onConfirm(obs); setBusy(false); }}>
          {busy && <Spinner color="currentColor" />} Confirmar pago
        </button>
      </>}
    >
      <p style={{ margin: 0, fontSize: 13 }}>
        Carpeta hecha por <strong>{payload.hecha_por_name || "—"}</strong> el{" "}
        <strong>{fmtDateTime(payload.hecha_at)}</strong>.
      </p>
      <label style={{ display: "block", marginTop: 16, fontSize: 13 }}>
        Observaciones (opcional)
        <textarea value={obs} onChange={(e) => setObs(e.target.value)}
                  rows={3} maxLength={500} disabled={busy}
                  placeholder='Ej. "Pago confirmado por transferencia 15/05"'
                  style={{ width: "100%", marginTop: 4 }} />
      </label>
    </CenteredModal>
  );
}

// ---------------------------------------------------------------------------
// RevertCobradaModal — sprint79.1. Flips pagada=true back to false.
// Carpeta row stays as hecha.
function RevertCobradaModal({ payload, onCancel, onConfirm }) {
  const [busy, setBusy] = useState(false);
  return (
    <CenteredModal
      open
      onClose={busy ? undefined : onCancel}
      title={`¿Revertir cobro de ${payload.case_number}?`}
      size="sm"
      footer={<>
        <button className="secondary" disabled={busy} onClick={onCancel}>Cancelar</button>
        <button disabled={busy}
                onClick={async () => { setBusy(true); await onConfirm(); setBusy(false); }}>
          {busy && <Spinner color="currentColor" />} Revertir cobro
        </button>
      </>}
    >
      <p style={{ margin: 0, fontSize: 13 }}>
        Esto deshace el marcado de pago{payload.pagada_at ? <> del <strong>{fmtDateTime(payload.pagada_at)}</strong></> : ""}.
        La carpeta sigue como hecha.
      </p>
    </CenteredModal>
  );
}

// ---------------------------------------------------------------------------
// BulkRevertCobradaModal — sprint79.1.
function BulkRevertCobradaModal({ count, onCancel, onConfirm }) {
  const [busy, setBusy] = useState(false);
  return (
    <CenteredModal
      open
      onClose={busy ? undefined : onCancel}
      title={`¿Revertir cobro de ${count} carpetas?`}
      size="sm"
      footer={<>
        <button className="secondary" disabled={busy} onClick={onCancel}>Cancelar</button>
        <button disabled={busy}
                onClick={async () => { setBusy(true); await onConfirm(); setBusy(false); }}>
          {busy && <Spinner color="currentColor" />} Revertir las {count}
        </button>
      </>}
    >
      <p style={{ margin: 0, fontSize: 13 }}>
        Cada carpeta vuelve al estado <strong>Hecha</strong> (sin cobro registrado). Las filas siguen registradas.
      </p>
    </CenteredModal>
  );
}

// ---------------------------------------------------------------------------
// BulkTransitionCobradaModal
function BulkTransitionCobradaModal({ count, onCancel, onConfirm }) {
  const [busy, setBusy] = useState(false);
  return (
    <CenteredModal
      open
      onClose={busy ? undefined : onCancel}
      title={`¿Marcar ${count} carpetas como cobradas?`}
      size="sm"
      footer={<>
        <button className="secondary" disabled={busy} onClick={onCancel}>Cancelar</button>
        <button disabled={busy}
                onClick={async () => { setBusy(true); await onConfirm(); setBusy(false); }}>
          {busy && <Spinner color="currentColor" />} Confirmar las {count}
        </button>
      </>}
    >
      <p style={{ margin: 0, fontSize: 13 }}>
        La atribución de cada carpeta (hecha por quién) queda como estaba; solo se marca el cobro.
      </p>
    </CenteredModal>
  );
}

// ---------------------------------------------------------------------------
// RevertModal
function RevertModal({ payload, onCancel, onConfirm }) {
  const [busy, setBusy] = useState(false);
  return (
    <CenteredModal
      open
      onClose={busy ? undefined : onCancel}
      title={`¿Revertir carpeta para ${payload.case_number}?`}
      size="sm"
      footer={<>
        <button className="secondary" disabled={busy} onClick={onCancel}>Cancelar</button>
        <button disabled={busy}
                onClick={async () => { setBusy(true); await onConfirm(); setBusy(false); }}
                style={{ background: "var(--color-danger)", color: "white", borderColor: "var(--color-danger)" }}>
          {busy && <Spinner color="currentColor" />} Revertir
        </button>
      </>}
    >
      <p style={{ margin: 0, fontSize: 13 }}>
        Esto deshace el marcado del <strong>{fmtDateTime(payload.hecha_at)}</strong> y vuelve el caso a pendientes.
      </p>
    </CenteredModal>
  );
}
